<template>
  <PModal
    :open="true"
    :title="title"
    :modal="false"
    :overlay="false"
    :portal="false"
    :transition="false"
    :close="false"
    :fullscreen="isSmallMq"
    :content="contentProps"
    :ui="{
      content:
        'surface safe-area @container top-0 left-0 h-full min-h-full w-screen max-w-none translate-x-0 translate-y-0 overflow-hidden shadow-sm ring-0 sm:h-auto sm:min-h-72 sm:w-auto sm:max-w-full sm:min-w-xl sm:rounded-t-lg md:min-w-2xl lg:min-w-3xl max-h-none'
    }"
    class="p-modal-draggable"
    @update:open="!$event && close()"
  >
    <template #content>
      <div ref="surface" class="flex h-full min-h-0 w-full flex-col">
        <div
          ref="dragBar"
          class="primary flex shrink-0 touch-none items-center justify-between gap-2 px-3 select-none"
          :class="isSmallMq ? '' : 'cursor-move'"
          data-modal-drag-handle
        >
          <span class="min-w-0 truncate font-medium"
            ><slot name="title">{{ title }}</slot></span
          >
          <PButton
            :icon="['fas', 'xmark']"
            size="lg"
            aria-label="Venster sluiten"
            @click.stop="close"
          />
        </div>
        <div class="min-h-0 w-full flex-1 overflow-auto"><slot /></div>
      </div>
    </template>
  </PModal>
</template>

<script setup lang="ts">
import { computed, nextTick, provide, ref, useTemplateRef, watch, type CSSProperties } from "vue"
import { storeToRefs } from "pinia"
import { useDraggable, useEventListener, useResizeObserver } from "@vueuse/core"
import "@interactjs/auto-start"
import "@interactjs/actions/resize"
import interact from "@interactjs/interact"
import PModal from "./PModal.vue"
import PButton from "@/components/elements/PButton.vue"
import { useWindowStore } from "@/store/windows.store"
import { useUserSettingsStore } from "@/store/user-settings.store"
import { useDeviceDetection } from "@/composables/utils/device-detection"

const props = withDefaults(defineProps<{ id: number; title?: string }>(), { title: "" })
const emit = defineEmits<{ close: [id: number]; loaded: [id: number] }>()

const windowStore = useWindowStore()
const userSettingsStore = useUserSettingsStore()
const { userSettings } = storeToRefs(userSettingsStore)
const { isSmallMq } = useDeviceDetection()
const surface = useTemplateRef<HTMLDivElement>("surface")
const dragBar = useTemplateRef<HTMLDivElement>("dragBar")
const modalElement = computed(
  () => surface.value?.closest<HTMLDivElement>('[role="dialog"]') ?? null
)
const thisWindow = computed(() => windowStore.getWindow(props.id))
const childLoaded = ref(false)
const hasPositioned = ref(false)
const closing = ref(false)
const zIndex = ref(windowStore.nextZIndex())
const size = ref({ width: 0, height: 0 })

const { x, y, position, isDragging } = useDraggable(modalElement, {
  handle: dragBar,
  disabled: () => isSmallMq.value || !hasPositioned.value,
  preventDefault: true,
  onStart: (_, event) => {
    if (
      (event.target as HTMLElement).closest("button, a, input, textarea, select, [contenteditable]")
    ) {
      return false
    }
    modalElement.value?.focus({ preventScroll: true })
  },
  onMove: (value) => Object.assign(value, boundedPosition(value.x, value.y)),
  onEnd: savePosition
})

provide("onChildLoaded", () => {
  childLoaded.value = true
})
provide("getModalDraggableElement", () => modalElement.value)
provide("close", close)
provide("window", thisWindow)

function bringToFront() {
  zIndex.value = windowStore.nextZIndex()
}

function close() {
  if (closing.value) return
  closing.value = true
  emit("close", props.id)
}

function onEscape(event: KeyboardEvent) {
  if (event.defaultPrevented) return
  event.preventDefault()
  event.stopPropagation()
  close()
}

// Keep enough of the header in view to move or close a partially off-screen window.
function boundedPosition(left: number, top: number) {
  const margin = Math.min(100, window.innerWidth, window.innerHeight)
  return {
    x: Math.max(margin - size.value.width, Math.min(left, window.innerWidth - margin)),
    y: Math.max(0, Math.min(top, window.innerHeight - margin))
  }
}

function moveTo(left: number, top: number) {
  position.value = boundedPosition(left, top)
}

function savePosition() {
  if (!userSettings.value || isSmallMq.value) return
  userSettings.value.windowPosX = x.value / window.innerWidth
  userSettings.value.windowPosY = y.value / window.innerHeight
  void userSettingsStore.saveUserSettings()
}

async function positionInitially() {
  const element = modalElement.value
  if (!childLoaded.value || !element || hasPositioned.value) return
  const rect = element.getBoundingClientRect()
  if (!isSmallMq.value) size.value = { width: rect.width, height: rect.height }
  const settings = userSettings.value
  const hasSavedPosition =
    settings &&
    settings.windowPosX >= 0 &&
    settings.windowPosX < 0.95 &&
    settings.windowPosY >= 0 &&
    settings.windowPosY < 0.95
  const left = hasSavedPosition
    ? settings.windowPosX * window.innerWidth
    : (window.innerWidth - rect.width) / 2
  const top = hasSavedPosition
    ? settings.windowPosY * window.innerHeight
    : (window.innerHeight - rect.height) / 2
  moveTo(left, top)
  // Offset windows that would otherwise open at the same position.
  for (const other of windowStore.windows) {
    if (
      other.id !== props.id &&
      x.value === other.lastPositionX &&
      y.value === other.lastPositionY
    ) {
      moveTo(x.value + 20, y.value + 20)
    }
  }
  hasPositioned.value = true
  emit("loaded", props.id)
  await nextTick()
  if (!closing.value) element.focus({ preventScroll: true })
}

watch([childLoaded, modalElement], positionInitially, { immediate: true, flush: "post" })
watch(
  position,
  (value) => {
    if (thisWindow.value) {
      thisWindow.value.lastPositionX = value.x
      thisWindow.value.lastPositionY = value.y
    }
  },
  { deep: true, flush: "sync" }
)

function onViewportResize() {
  if (hasPositioned.value && !isSmallMq.value) moveTo(x.value, y.value)
}
useEventListener("resize", onViewportResize)
watch(isSmallMq, onViewportResize)
useResizeObserver(modalElement, () => {
  if (!hasPositioned.value || isSmallMq.value || !modalElement.value) return
  const rect = modalElement.value.getBoundingClientRect()
  size.value = { width: rect.width, height: rect.height }
  moveTo(x.value, y.value)
})

// Preserve resizing on all four edges; moving the window itself is owned by VueUse.
watch(
  [modalElement, isSmallMq],
  ([element, small], _, onCleanup) => {
    if (!element || small) return
    const interaction = interact(element).resizable({
      margin: 4,
      edges: { top: true, left: true, bottom: true, right: true },
      listeners: {
        move(event) {
          size.value = { width: event.rect.width, height: event.rect.height }
          moveTo(x.value + event.deltaRect.left, y.value + event.deltaRect.top)
        }
      }
    })
    onCleanup(() => interaction.unset())
  },
  { immediate: true, flush: "post" }
)

const contentProps = computed(() => ({
  onPointerdownCapture: bringToFront,
  onInteractOutside: (event: Event) => event.preventDefault(),
  onKeydown: (event: KeyboardEvent) => {
    if (event.key === "Escape") onEscape(event)
  },
  style: {
    zIndex: zIndex.value,
    top: isSmallMq.value ? "0px" : `${y.value}px`,
    left: isSmallMq.value ? "0px" : `${x.value}px`,
    width: isSmallMq.value ? "100vw" : size.value.width ? `${size.value.width}px` : undefined,
    height: isSmallMq.value ? "100dvh" : size.value.height ? `${size.value.height}px` : undefined,
    visibility: hasPositioned.value ? "visible" : "hidden",
    opacity: isDragging.value ? 0.8 : 1,
    translate: "none"
  } satisfies CSSProperties
}))
</script>
